import { AfterViewInit, Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { UserService, OrganizationService, AppService, HelperService, FitnessService, TrainingPlanService, StorageService, ExerciseService, TranslateConfigService } from 'src/app/services';
import { ModalController, AlertController, IonRouterOutlet, ToastController, PopoverController } from '@ionic/angular';
import { WorkoutProgramSettingsPage } from './workout-program-settings/workout-program-settings.page';
import { Organization, User, IWorkoutTemplateDetail } from 'src/app/models';
import { NotesPage } from 'src/app/components/notes/notes.page';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import * as Sentry from "@sentry/angular-ivy";
import moment from 'src/app/utils/moment';
import { ExerciseSelectPage } from 'src/app/modals/exercise-select/exercise-select.page';
import { WorkoutSelectComponent } from 'src/app/modals/workout-select/workout-select.component';
import { getProgramDatePopoverSide, ProgramDatePopoverComponent } from 'src/app/components/program-date-popover/program-date-popover.component';
import { IWorkoutProgramDetail, WorkoutProgram, WorkoutProgramKind } from 'src/app/models/training/workout-program.model';
import { TrainerWorkout, TrainerWorkoutCreate, SplitSecondaryMuscle } from 'src/app/models/training/workout.model';
import { ChartData, ChartOptions, ChartType } from 'chart.js';
import { isAccountDeactivatedForBillingStatus } from 'src/app/shared/billing/organization-status-policy';

@Component({
  selector: 'app-workout-program-builder',
  templateUrl: './workout-program-builder.page.html',
  styleUrls: ['./workout-program-builder.page.scss'],
})
export class WorkoutProgramBuilderPage implements OnInit, AfterViewInit {
  /** When true, builder is embedded (e.g. split-screen compare); close uses `comparePaneClose` instead of dismissing the parent modal. */
  @Input() comparePaneEmbedded = false;
  @Output() comparePaneClose = new EventEmitter<any>();

  @Input() modalContext?: {
    workoutProgramUser?: string;
    id?: string;
    type?: 'user' | 'library';
    mode?: 'program' | 'template' | 'workout';
    duplicate?: string;
    source?: string;
    template?: string;
    kind?: string;
  };

  public actionSheet: HTMLIonActionSheetElement;
  public activeUser: User;
  public activeWorkout: any = {};
  public activeWorkoutIndex: number;
  public activeWorkoutProgram: IWorkoutProgramDetail = new WorkoutProgram({ workouts: [], notes: [] }).cloneActive();
  public configSet: boolean = false;
  public dragDisabled = false;
  public equipmentList;
  public hasDraft = false;
  public mode: "program" | "template" | "workout";
  public newSetType: string = null;
  public programUserId: string;
  public programUser: User;
  public programId: string;
  public saveMessageEnabled = true;
  public workoutProgram: WorkoutProgram;
  public organization: Organization;
  public isPremium = false;
  public programType: "user" | "library";
  public versionHistory = [];
  /** When "split", days use primary/secondary muscles + sets instead of exercises. */
  public workoutProgramKind: WorkoutProgramKind = 'program';
  /** Flat list of muscle groups (body parts) for split program dropdowns. */
  public muscleGroups: { value: string; name?: string }[] = [];
  public splitEditorChartType: ChartType = 'doughnut';
  public splitEditorChartOptions: ChartOptions<'doughnut'> = this.getSplitEditorChartOptions();
  public splitEditorChartData: ChartData<'doughnut'> = { labels: [], datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }] };
  public splitEditorLegendItems: { label: string; sets: number; color: string; role: 'primary' | 'secondary' }[] = [];
  public splitEditorHasChartData = false;
  public isWorkoutDrawerOpen = false;

  constructor(
    private alertController: AlertController,
    private appService: AppService,
    private fitnessService: FitnessService,
    private helperService: HelperService,
    private modalCtrl: ModalController,
    private organizationService: OrganizationService,
    private popoverCtrl: PopoverController,
    @Optional() private routerOutlet: IonRouterOutlet,
    private storageService: StorageService,
    private toastCtrl: ToastController,
    private userService: UserService,
    private trainingPlanService: TrainingPlanService,
    private translateConfig: TranslateConfigService,
    private exerciseService: ExerciseService,
  ) {
    this.activeUser = userService.getAppUser();
    this.organization = organizationService.getAppOrganization();
  }

  ngOnInit() {
    if (this.routerOutlet) {
      this.routerOutlet.swipeGesture = false;
    }
    this.storageService.get("workoutProgramBuilderMessageDisabled").then((val) => {
      if(val === true) {
        this.saveMessageEnabled = false;
      }
    }); 
  }

  /**
   * Builder is only opened via `WorkoutProgramBuilderModalService` + `modalContext`.
   * (Legacy `/client/.../workout-program-builder` and tab routes were removed.)
   */
  private buildProgramEntrySource(): Record<string, any> {
    const ctx = { ...((this.modalContext || {}) as Record<string, any>) };
    if (!ctx.userId && ctx.workoutProgramUser) {
      ctx.userId = ctx.workoutProgramUser;
    }
    return ctx;
  }

  async ionViewDidEnter() {
    if (this.comparePaneEmbedded) return;
    await this.runWorkoutBuilderEntry();
  }

  ngAfterViewInit(): void {
    if (this.comparePaneEmbedded) {
      void this.runWorkoutBuilderEntry();
    }
  }

  private dismissBuilder(data?: any): void {
    if (this.comparePaneEmbedded) {
      this.comparePaneClose.emit(data);
      return;
    }
    void this.modalCtrl.dismiss(data);
  }

  private async runWorkoutBuilderEntry() {
    if (isAccountDeactivatedForBillingStatus(this.organization?.status)) {
      await this.appService.showToastWithDuration(this.translateConfig.t('general.accountDeactivatedCannotEdit'), null, "danger");
      this.dismissBuilder();
      return;
    }

    const sourceParams = this.buildProgramEntrySource();

    this.programType = (sourceParams.type as any) || 'user';
    this.programUserId =
      sourceParams.userId ||
      sourceParams.workoutProgramUser ||
      this.activeUser.uid;
    if (this.programUserId) {
      this.programUser = await this.userService.getUserAsPromise(this.programUserId);
      this.loadUserEquipment();
    }
    this.programId = sourceParams.id || 'add';
    this.mode = (sourceParams.mode as any) || 'program';
    if(!this.organization || !this.organization.isUserTrainer(this.activeUser.uid)) {
      this.isPremium = true;
    }
    if (this.programId === "add") {
      const duplicateId = sourceParams.duplicate;
      if (duplicateId) {
        await this.appService.presentLoading();
        try {
          const dupSource = sourceParams.source;
          const source = dupSource ? dupSource : this.programType === "library" ? "library" : "user";
          if (source === "library") {
            if (this.mode === "program") {
              this.workoutProgram = await this.trainingPlanService.getOrganizationWorkoutProgramAsPromise(this.organization.uid, duplicateId)
            } else if (this.mode === "template") {
              let template = await this.trainingPlanService.getOrganizationWorkoutTemplateAsPromise(this.organization.uid, duplicateId)
              if (template) {
                this.workoutProgram = new WorkoutProgram({
                  id: template.id,
                  goal: template.goal || null,
                  title: template.title || null,
                  level: template.level || null,
                  description: template.description,
                  workouts: []
                })
                this.cleanSets();
              }
            } else if (this.mode === "workout") {
              const workout = await this.trainingPlanService.getOrganizationWorkoutAsPromise(this.organization.uid, duplicateId);
              if(workout) {
                this.workoutProgram = new WorkoutProgram( {
                  title: workout.title,
                  workouts: [{
                    title: workout.workoutTitle || "Workout",
                    sets: workout.sets || [],
                  }],
                  focus: workout.focus || null,
                  level: workout.level || null,
                  gender: workout.gender || null,
                  goal: workout.goal || null,
                });
                this.cleanSets();
              }
            }
          } else {
            this.workoutProgram = await this.trainingPlanService.getWorkoutProgramAsPromise(this.programUserId, duplicateId);
          }
          this.activeWorkoutProgram = this.workoutProgram.hasDraft() ? new WorkoutProgram(this.workoutProgram.cloneDraft()) : new WorkoutProgram(this.workoutProgram.cloneActive());
          this.activeWorkoutProgram.title = this.activeWorkoutProgram.title;

          delete this.activeWorkoutProgram.startDate;
          delete this.activeWorkoutProgram.endDate;
          delete this.activeWorkoutProgram.id;
        } catch (error) {
          Sentry.captureException(error.originalError || error);
          console.log(error);
          Sentry.captureException(error.originalError || error);
          await this.appService.showError("An error has occurred. Our support team has been notified.");
          this.goBack();
        } finally {
          await this.appService.hideLoading();
        }
      }
      if (!duplicateId && sourceParams.kind === 'split') {
        this.activeWorkoutProgram.workoutProgramKind = 'split';
      }
      const templateId = sourceParams.template;
      if (this.mode === "program" && templateId) {
        const template: IWorkoutTemplateDetail = await this.trainingPlanService.getOrganizationWorkoutTemplateAsPromise(this.organization.uid, templateId);
        const workout: any = template.workout || {};
        workout.sets = workout.sets || [];
        for (const set of workout.sets) {
          this.trainingPlanService.cleanSet(set);
        }
        this.activeWorkoutProgram.workouts = [workout];
        this.activeWorkoutProgram.title = template.title || null;
        this.activeWorkoutProgram.level  = template.level || null;
        this.activeWorkoutProgram.goal = template.goal || null;
      }
      if(this.mode === "template" || this.mode === "workout") {
        this.addWorkout("");
      }
      delete this.activeWorkoutProgram.createdBy;
      delete this.activeWorkoutProgram.creationDate;
      const defaultStartTime: string | null = this.organization?.getSetting("programs")?.defaultStartTime || null;
      const startMoment = (moment as any)();
      if (defaultStartTime) {
        const [hourStr] = defaultStartTime.split(":");
        const hour = parseInt(hourStr, 10);
        if (!Number.isNaN(hour)) {
          startMoment.hour(hour).minute(0).second(0).millisecond(0);
        }
      }
      this.activeWorkoutProgram.startDate = startMoment.toISOString();
      this.activeWorkoutProgram.startDateHidden = this.organization?.getSetting("programs")?.startDateHidden || false;
      this.activeWorkoutProgram.endDateHidden = this.organization?.getSetting("programs")?.endDateHidden || false;
      this.activeWorkoutProgram.notes = this.activeWorkoutProgram.notes && this.activeWorkoutProgram.notes.length > 0 ? this.activeWorkoutProgram.notes : (this.organization?.settings?.programs?.workoutPrograms?.defaultNotes || []);
      this.activeWorkoutProgram.gender = this.activeWorkoutProgram.gender || this.programUser.gender || null;
      this.showProgramOptions();
    } else {
      this.configSet = true;
      await this.loadWorkoutProgram();
    }
    this.workoutProgramKind = this.activeWorkoutProgram?.workoutProgramKind ?? 'program';
    if (this.workoutProgramKind === 'split') {
      await this.loadMuscleGroups();
    }
  }

  private getSplitEditorChartOptions(): ChartOptions<'doughnut'> {
    const textColor = typeof document !== 'undefined' && document.documentElement
      ? getComputedStyle(document.documentElement).getPropertyValue('--ion-text-color').trim() || '#ffffff'
      : '#ffffff';
    return {
      responsive: true,
      maintainAspectRatio: true,
      cutout: '55%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 10,
            color: textColor
          }
        }
      }
    };
  }

  private getChartColor(cssVar: string): string {
    if (typeof document === 'undefined' || !document.documentElement) return '#888';
    const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar.replace('var(', '').replace(')', '').trim()).trim();
    return value || '#888';
  }

  private getSecondaryColorPalette(): string[] {
    return [
      '#d97706',
      '#7c3aed',
      '#0d9488',
      '#db2777',
      '#dc2626',
      '#059669',
      '#ea580c',
      '#4f46e5',
    ];
  }

  private buildSplitEditorChartData(): ChartData<'doughnut'> {
    if (!(this.workoutProgramKind === 'split' && this.activeWorkout?.type === 'workout')) {
      return { labels: [], datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }] };
    }
    const labels: string[] = [];
    const data: number[] = [];
    const colors: string[] = [];
    const primarySplitColor = '#2563eb';
    const secondaryPalette = this.getSecondaryColorPalette();

    if (this.activeWorkout.primaryMuscle && (this.activeWorkout.primarySets != null && Number(this.activeWorkout.primarySets) > 0)) {
      labels.push(this.getMuscleLabel(this.activeWorkout.primaryMuscle));
      data.push(Number(this.activeWorkout.primarySets));
      colors.push(primarySplitColor);
    }

    (this.activeWorkout.secondaryMuscles || []).forEach((sec: { muscle?: string; sets?: number }, idx: number) => {
      if (sec?.muscle && (sec?.sets != null && Number(sec.sets) > 0)) {
        labels.push(this.getMuscleLabel(sec.muscle));
        data.push(Number(sec.sets));
        colors.push(secondaryPalette[idx % secondaryPalette.length]);
      }
    });

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: colors,
        hoverBackgroundColor: colors.map(c => c),
        borderWidth: 0
      }]
    };
  }

  private buildSplitEditorLegendItems(): { label: string; sets: number; color: string; role: 'primary' | 'secondary' }[] {
    if (!(this.workoutProgramKind === 'split' && this.activeWorkout?.type === 'workout')) {
      return [];
    }
    const items: { label: string; sets: number; color: string; role: 'primary' | 'secondary' }[] = [];
    const primarySplitColor = '#2563eb';
    const secondaryPalette = this.getSecondaryColorPalette();

    if (this.activeWorkout.primaryMuscle && (this.activeWorkout.primarySets != null && Number(this.activeWorkout.primarySets) > 0)) {
      items.push({
        label: this.getMuscleLabel(this.activeWorkout.primaryMuscle),
        sets: Number(this.activeWorkout.primarySets),
        color: primarySplitColor,
        role: 'primary'
      });
    }

    (this.activeWorkout.secondaryMuscles || []).forEach((sec: { muscle?: string; sets?: number }, idx: number) => {
      if (sec?.muscle && (sec?.sets != null && Number(sec.sets) > 0)) {
        items.push({
          label: this.getMuscleLabel(sec.muscle),
          sets: Number(sec.sets),
          color: secondaryPalette[idx % secondaryPalette.length],
          role: 'secondary'
        });
      }
    });
    return items;
  }

  private refreshSplitEditorChartState() {
    this.splitEditorChartData = this.buildSplitEditorChartData();
    this.splitEditorLegendItems = this.buildSplitEditorLegendItems();
    const dataset = this.splitEditorChartData?.datasets?.[0]?.data || [];
    this.splitEditorHasChartData = dataset.some(v => Number(v) > 0);
  }

  ionViewDidLeave() {
    this.storageService.remove("exerciseSelectFilters");
  }

  cleanSets() {
    this.workoutProgram.workouts = this.workoutProgram.workouts || [];
    for (const workout of this.workoutProgram.workouts) {
      workout.sets = workout.sets || [];
      for (const set of workout.sets) {
        this.trainingPlanService.cleanSet(set);
      }
    }
  }

  async loadWorkoutProgram() {
    if (this.programType === "user") {
      this.workoutProgram = await this.trainingPlanService.getWorkoutProgramAsPromise(this.programUserId, this.programId);
    }
    if (this.programType === "library") {
      if (this.mode === "program") {
        this.workoutProgram = await this.trainingPlanService.getOrganizationWorkoutProgramAsPromise(this.organization.uid, this.programId);
      } else if (this.mode === "template") {
        const template = await this.trainingPlanService.getOrganizationWorkoutTemplateAsPromise(this.organization.uid, this.programId);
        this.workoutProgram = new WorkoutProgram({
          id: template.id,
          goal: template.goal || null,
          title: template.title || null,
          level: template.level || null,
          description: template.description,
          workouts: [{
            sets: template.workout?.sets || [],
          }]
        });
      } else if (this.mode === "workout") {
        const template = await this.trainingPlanService.getOrganizationWorkoutAsPromise(this.organization.uid, this.programId);
        const workoutProgram = {
          id: template.id,
          title: template.title,
          workouts: [{
            title: template.workoutTitle || "Workout",
            sets: template.sets || [],
          }],
          focus: template.focus || null,
          level: template.level || null,
          gender: template.gender || null,
          goal: template.goal || null,
        };
        this.workoutProgram = new WorkoutProgram(workoutProgram);
      }
    }
    if (!this.workoutProgram) {
      await this.appService.showToastWithDuration('This training program could not be found.', null, 'danger');
      this.goBack();
      return;
    }
    delete this.workoutProgram.comments;
    this.cleanSets();
    this.activeWorkoutProgram = this.workoutProgram.hasDraft()? this.workoutProgram.cloneDraft() : this.workoutProgram.cloneActive();
    this.hasDraft = this.workoutProgram.published && this.workoutProgram.hasDraft();
    if (this.activeWorkoutProgram && this.activeWorkoutProgram.workouts) {
      this.setActiveWorkout(0)
      const toSave = this.helperService.copyObject(this.activeWorkoutProgram);
      this.versionHistory.push(toSave);
    }
  }

  async loadUserEquipment() {
    this.equipmentList = await this.userService.getEquipmentList(this.programUserId);
  }

  async loadMuscleGroups() {
    const hierarchical = await this.exerciseService.getSystemBodyParts();
    if (hierarchical?.length) {
      // Only allow selecting leaf nodes (exclude parents that have children, e.g. Upper/Lower Body)
      const flat = this.helperService.collapseChildren(hierarchical);
      this.muscleGroups = flat
        .filter(m => !m?.children?.length)
        .sort((a, b) => {
          const aLabel = (a?.name || a?.value || '').toString();
          const bLabel = (b?.name || b?.value || '').toString();
          return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
        });
    }
    this.refreshSplitEditorChartState();
  }

  getProgramOrganizationSetting(settingName: string) {
    return this.organization?.settings?.programs?.workoutPrograms[settingName]
  }

  addWorkout(title: string, sets = [], restDay = false) {
    this.activeWorkoutProgram.workouts = this.activeWorkoutProgram.workouts || [];
    const day: any = {
      title: title,
      sets: restDay || this.workoutProgramKind !== 'split' ? sets : [],
      type: restDay ? "rest" : "workout"
    };
    if (this.workoutProgramKind === 'split' && !restDay) {
      day.primaryMuscle = null;
      day.primarySets = null;
      day.secondaryMuscles = [];
    }
    this.activeWorkoutProgram.workouts.push(day);
    this.saveWorkoutProgram();
  }

  setActiveWorkout(index) {
    this.activeWorkout = this.activeWorkoutProgram.workouts[index];
    this.activeWorkoutIndex = index;
    if (this.workoutProgramKind === 'split' && this.activeWorkout?.type === 'workout') {
      if (!Array.isArray(this.activeWorkout.secondaryMuscles)) {
        this.activeWorkout.secondaryMuscles = [];
      }
      if (!this.activeWorkout.secondaryMuscles.length) {
        this.activeWorkout.secondaryMuscles.push({ muscle: '', sets: null });
      }
    }
    this.refreshSplitEditorChartState();
  }

  openWorkoutDrawer() {
    this.isWorkoutDrawerOpen = !this.isWorkoutDrawerOpen;
  }

  onWorkoutSelected(index: number) {
    this.setActiveWorkout(index);
    this.isWorkoutDrawerOpen = false;
  }

  editWorkoutFromDrawer(index: number): void {
    this.setActiveWorkout(index);
    this.editWorkoutTitle();
  }

  duplicateWorkoutFromDrawer(index: number): void {
    this.setActiveWorkout(index);
    this.duplicateWorkout(index);
  }

  saveWorkoutToLibraryFromDrawer(index: number): void {
    this.setActiveWorkout(index);
    this.saveWorkoutToLibrary();
  }

  deleteWorkoutFromDrawer(index: number): void {
    this.setActiveWorkout(index);
    this.deleteWorkout();
  }

  async addExercises() {
    if (this.mode === "template") {
      this.activeWorkout.sets = this.activeWorkout.sets || [];
      this.activeWorkout.sets.push({
        type: "straightSet",
        sets: null,
        rest: "60 seconds",
        exercises: [{
          reps: null,
          tempo: null
        }]
      });
    } else {
      const modal = await this.modalCtrl.create({
        component: ExerciseSelectPage,
        backdropDismiss: false,
        componentProps: {
          multiple: true,
          userId: this.programUserId,
          equipmentList: this.equipmentList
        }
      });
      await modal.present();
      const { data, role } = await modal.onWillDismiss();
      if (role === 'selected' && data?.exercises) {
        this.activeWorkout.sets = this.activeWorkout.sets || [];
        for (const ex of data.exercises) {
          this.activeWorkout.sets.push({
            type: "straightSet",
            sets: null,
            rest: "60",
            restType: "sec",
            exercises: [{
              id: ex.id,
              reps: null,
              tempo: null,
              link: ex.link || null,
              notes: ex.notes || null,
            }]
          });
        }
        this.saveWorkoutProgram();
      }
    }
  }

  duplicateSet(set, index) {
    this.activeWorkout.sets.splice(index, 0, this.helperService.copyObject(set));
    this.saveWorkoutProgram();
  }

  async copySetToWorkout(set) {
    const inputs = [];
    for (let i = 0; i < this.activeWorkoutProgram.workouts.length; i++) {
      const workout = this.activeWorkoutProgram.workouts[i];
      inputs.push({
        name: `workout-${i}`,
        type: 'checkbox',
        label: workout.title || i + 1,
        value: i
      });
    }
    const alert = await this.alertController.create({
      header: 'Select Workouts',
      inputs: inputs,
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
        }, {
          text: 'Copy',
          handler: (vals) => {
            if(vals?.length > 0) {
              for (const WI of vals) {
                this.activeWorkoutProgram.workouts[WI].sets = this.activeWorkoutProgram.workouts[WI].sets || [];
                this.activeWorkoutProgram.workouts[WI].sets.push(this.helperService.copyObject(set));
              }
            }
          }
        }
      ]
    });
    await alert.present();
  }

  removeSet(set, index) {
    this.activeWorkout.sets.splice(index, 1);
    this.saveWorkoutProgram()
  }

  reorderSet(event) {
    const itemMove = this.activeWorkout.sets.splice(event.detail.from, 1)[0];
    this.activeWorkout.sets.splice(event.detail.to, 0, itemMove);
    this.saveWorkoutProgram();
    event.detail.complete();
  }

  newRestDay() {
    this.addWorkout("Rest Day", null, true);
    this.setActiveWorkout(this.activeWorkoutProgram.workouts.length - 1);
    this.saveWorkoutProgram();
  }

  async newWorkout() {
    const alert = await this.alertController.create({
      header: 'Workout Title',
      inputs: [
        {
          name: 'workoutTitle',
          type: 'text',
          placeholder: 'e.g. Upper Body Day'
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            if (values.workoutTitle) {
              this.addWorkout(values.workoutTitle);
              this.setActiveWorkout(this.activeWorkoutProgram.workouts.length - 1);
              this.saveWorkoutProgram();
            } else {
              const failAert = await this.alertController.create({
                header: 'Workout Title Missing',
                message: 'You must enter a workout title',
                buttons: ['OK']
              });
              await failAert.present();
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async selectWorkout() {
    if(["standard",  "pro", "platinum"].indexOf(this.organization.plan) > -1) {
      const modal = await this.modalCtrl.create({
        component: WorkoutSelectComponent,
        componentProps: {
          source: "organization"
        }
      });
      await modal.present();
      const { data, role } = await modal.onWillDismiss();
      if (role === "selected" && data.workout) {
        const program: TrainerWorkout = data.workout;
        this.addWorkout(program.workoutTitle || "", program.sets || null);
        this.setActiveWorkout(this.activeWorkoutProgram.workouts.length - 1);
        this.saveWorkoutProgram();
      }
    } else {
      this.appService.showUpgradeModal();
    }
  }

  duplicateWorkout(currentIndex) {
    const toCopy = this.helperService.copyObject(this.activeWorkout);
    this.activeWorkoutProgram.workouts.splice(currentIndex + 1, 0, toCopy);
    this.setActiveWorkout(currentIndex + 1);
    this.saveWorkoutProgram();
  }

  async saveWorkoutToLibrary() {
    const modal = await this.modalCtrl.create({
      backdropDismiss: false,
      component: WorkoutProgramSettingsPage,
      componentProps: {
        type: "workout",
        mode: "workout",
        action: "add",
        workoutProgram: new WorkoutProgram({}),
        programGoals: this.getProgramOrganizationSetting("categories"),
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === 'submitted') {
      const toSave: TrainerWorkoutCreate = {
        sets: this.activeWorkout.sets || null,
        title: data.data.title || null,
        gender: data.data.gender || null,
        focus: data.data.focus || null,
        goal: data.data.goal || null,
        level: data.data.level || null,
        workoutTitle: this.activeWorkout.title || null,
      };
      await this.trainingPlanService.publishOrganizationWorkout(this.organization.uid, null, toSave, this.activeUser.uid);
      await this.appService.showToastWithDuration("Workout successfully saved to library", null, "success")
    }
  }

  deleteWorkout() {
    this.activeWorkoutProgram.workouts.splice(this.activeWorkoutIndex, 1);
    this.setActiveWorkout(this.activeWorkoutProgram.workouts[this.activeWorkoutIndex] ? this.activeWorkoutIndex : 0);
    this.saveWorkoutProgram();
  }

  async editWorkoutTitle() {
    let value = this.activeWorkout.title;
    const alert = await this.alertController.create({
      header: 'Edit Category Title',
      inputs: [
        {
          name: 'planTitle',
          type: 'text',
          value: value,
          placeholder: `e.g. Non Workout Days`,
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            let planTitle = values.planTitle
            if (planTitle) {
              this.activeWorkout.title = planTitle;
              this.saveWorkoutProgram();
            } else {
              return
            }
          }
        }
      ]
    });
    await alert.present();
  }

  getProgramSettings() {
    return {
      title: this.activeWorkoutProgram.title || null,
      startDate: this.activeWorkoutProgram.startDate || null,
      startDateHidden: this.helperService.valueExists(this.activeWorkoutProgram.startDateHidden)? this.activeWorkoutProgram.startDateHidden : false,
      endDate: this.activeWorkoutProgram.endDate || null,
      endDateHidden: this.helperService.valueExists(this.activeWorkoutProgram.endDateHidden)? this.activeWorkoutProgram.endDateHidden : false,
      focus: this.activeWorkoutProgram.focus || null,
      level: this.activeWorkoutProgram.level || null,
      gender: this.activeWorkoutProgram.gender || null,
      goal: this.activeWorkoutProgram.goal || null,
      workouts: this.activeWorkoutProgram.workouts || [],
      workoutProgramKind: this.activeWorkoutProgram.workoutProgramKind ?? 'program'
    }
  }

  setProgramSettings(obj) {
    Object.assign(this.activeWorkoutProgram, obj);
  }

  async showProgramOptions() {
    const action = this.configSet ? "edit" : "add";
    const modal = await this.modalCtrl.create({
      backdropDismiss: false,
      component: WorkoutProgramSettingsPage,
      componentProps: {
        type: this.programType,
        mode: this.mode,
        action: action,
        workoutProgram: this.getProgramSettings(),
        programGoals: this.getProgramOrganizationSetting("categories"),
        premium: this.isPremium
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === 'submitted') {
      this.setProgramSettings(data.data);
      this.setActiveWorkout(0);
      this.configSet = true;
      this.saveWorkoutProgram();
    } else if (!this.configSet) {
      setTimeout(() => {
        this.goBack();
      }, 500);
    } else { }
  }

  async showNotes() {
    let notes = this.activeWorkoutProgram.notes;
    const modal = await this.modalCtrl.create({
      component: NotesPage,
      backdropDismiss: false,
      componentProps: {
        defaultNotes: this.organization?.settings?.programs?.workoutPrograms?.defaultNotes,
        action: "edit",
        notes: notes
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === "submitted") {
      this.activeWorkoutProgram.notes = data.data;
      this.saveWorkoutProgram();
    }
  }

  disableSaveMessage() {
    this.storageService.set("workoutProgramBuilderMessageDisabled", true);
    this.saveMessageEnabled = false;
  }

  async showSaveMessage() {
    if(!this.saveMessageEnabled) {
      return
    }
    const toast = await this.toastCtrl.create({
      message: "Your training program has been automatically saved",
      buttons: [
        {
          text: "Don't show again",
          role: 'cancel',
          handler: () => {
            this.disableSaveMessage();
          }
        }
      ],
      position: 'bottom',
      color: "success",
      duration: 3000,
    });
    toast.present();
  }

  async saveWorkoutProgram() {
    try {
      this.refreshSplitEditorChartState();
      const toSave = this.helperService.copyObject(this.activeWorkoutProgram);
      this.versionHistory.push(toSave);
      if (this.programType === "user") {
        let res = await this.trainingPlanService.saveUserWorkoutProgram(this.organization?.uid, this.programUserId, this.programId, toSave, this.activeUser.uid);
        if (this.programId === "add") {
          this.programId = res.key;
        }
        if(this.workoutProgram?.published) {
          this.hasDraft = true;
        }
      } else {
        let res;
        if (this.mode === "program") {
          res = await this.trainingPlanService.saveOrganizationWorkoutProgram(this.organization?.uid, this.programId, toSave, this.activeUser.uid);
          if(this.workoutProgram?.published) {
            this.hasDraft = true;
          }
        } else {
          return
        }
        if (this.programId === "add") {
          this.programId = res.key;
        }
      }
      this.showSaveMessage();
    } catch (error) {
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration("Unable to save program. An unexpected error ocurred");
    }
  }

  async publishWorkoutProgram() {
    await this.appService.presentLoading();
    try {
      // Publish the workout program
      if (this.programType === "user") {
        await this.trainingPlanService.publishUserWorkoutProgram(this.organization?.uid, this.programUserId, this.programId, this.activeWorkoutProgram, this.activeUser.uid);
        this.dismissBuilder({ status: 'published', programId: this.programId });
      }
      // Publish the workout program to the library
      if (this.programType === "library")  {
        // Publish the workout program to the training programs
        if (this.mode === "program") {
          const key = await this.trainingPlanService.publishOrganizationWorkoutProgram(this.organization?.uid, this.programId, this.activeWorkoutProgram, this.activeUser.uid);
          this.dismissBuilder({ status: 'published', programId: key, mode: this.mode });

        } else if (this.mode === "template") {
          const template = this.helperService.copyObject(this.activeWorkoutProgram);
          template.workout = {
            sets: this.activeWorkoutProgram.workouts[0].sets || []
          }
          delete template.workouts;
          const key = await this.trainingPlanService.publishOrganizationWorkoutTemplate(this.organization?.uid, this.programId, template, this.activeUser.uid);
          await this.dismissBuilder({ status: 'published', programId: key, mode: this.mode });
        } else if(this.mode === "workout") {
          const copy = this.helperService.copyObject(this.activeWorkoutProgram);
          const workout = {
            title: copy.title || "",
            workoutTitle: this.activeWorkoutProgram.workouts[0].title || null,
            sets: this.activeWorkoutProgram.workouts[0].sets || [],
            focus: this.activeWorkoutProgram.focus || null,
            level: this.activeWorkoutProgram.level || null,
            gender: this.activeWorkoutProgram.gender || null, 
            goal: this.activeWorkoutProgram.goal || null,
          }
          const res = await this.trainingPlanService.publishOrganizationWorkout(this.organization?.uid, this.programId, workout, this.activeUser.uid);
          this.dismissBuilder({ status: 'published', programId: res, mode: this.mode });
        }
      }

      this.appService.showToastWithDuration("Successfully published training program", null, "success");
    } catch (error) {
      console.log(error);
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    } finally {
      await this.appService.hideLoading();
    }
  }

  undoRevision() {
    const historyIndex = this.versionHistory.length - 1;
    this.activeWorkoutProgram = this.versionHistory[historyIndex - 1];
    this.setActiveWorkout(this.activeWorkoutIndex);
    this.versionHistory.splice(historyIndex - 1, 2);
    this.saveWorkoutProgram();
  }

  async revertDraft() {
    this.activeWorkoutProgram = this.workoutProgram.cloneActive();
    if (this.activeWorkoutProgram && this.activeWorkoutProgram.workouts) {
      this.activeWorkout = this.activeWorkoutProgram.workouts[0]
      this.activeWorkoutIndex = 0;
      const toSave = this.helperService.copyObject(this.activeWorkoutProgram);
      this.versionHistory = [toSave];
    }
    try {
      if (this.programType === "user") {
        await this.trainingPlanService.publishUserWorkoutProgram(this.organization.uid, this.programUserId, this.programId, this.activeWorkoutProgram, this.activeUser.uid, false);
      }
      if (this.programType === "library") {
        if (this.mode === "program") {
          await this.trainingPlanService.publishOrganizationWorkoutProgram(this.organization.uid, this.programId, this.activeWorkoutProgram, this.activeUser.uid);
        } else if (this.mode === "template") {
          const template = this.helperService.copyObject(this.activeWorkoutProgram);
          template.workout = {
            sets: this.activeWorkoutProgram.workouts[0].sets || []
          }
          delete template.workouts;
          await this.trainingPlanService.publishOrganizationWorkoutTemplate(this.organization.uid, this.programId, template, this.activeUser.uid);
        }
      }
      this.appService.showToastWithDuration("Successfully reverted training program", null, "success");
      this.hasDraft = false;
    } catch (error) {
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    } finally {
      await this.appService.hideLoading();
    }
  }

  isTrainer() {
    return this.activeUser && this.organization.isUserTrainer(this.activeUser.uid);
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.activeWorkoutProgram.workouts, event.previousIndex, event.currentIndex);
    this.setActiveWorkout(event.currentIndex);
    this.saveWorkoutProgram();
  }

  disableDrag() {
    this.dragDisabled = true;
  }

  enableDrag() {
    this.dragDisabled = false;
  }

  getActiveVolume() {
    if (this.workoutProgramKind === 'split' && this.activeWorkout?.type === 'workout') {
      const primary = Number(this.activeWorkout.primarySets) || 0;
      const secondary = (this.activeWorkout.secondaryMuscles || []).reduce((sum, s) => sum + (Number(s.sets) || 0), 0);
      return primary + secondary;
    }
    return this.fitnessService.calculateWorkoutVolume(this.activeWorkout);
  }

  addSecondaryMuscle() {
    if (!this.activeWorkout.secondaryMuscles) {
      this.activeWorkout.secondaryMuscles = [];
    }
    this.activeWorkout.secondaryMuscles.push({ muscle: '', sets: null });
    this.saveWorkoutProgram();
  }

  removeSecondaryMuscle(index: number) {
    this.activeWorkout.secondaryMuscles.splice(index, 1);
    this.saveWorkoutProgram();
  }

  getMuscleLabel(value: string): string {
    if (!value) return '';
    const found = this.muscleGroups.find(m => m.value === value);
    return found?.name ?? found?.value ?? value;
  }

  goBack() {
    this.dismissBuilder();
  }

  async editDate(event, type: "startDate" | "endDate") {
    const popover = await this.popoverCtrl.create({
      component: ProgramDatePopoverComponent,
      event: event,
      cssClass: 'popover-full',
      side: getProgramDatePopoverSide(event),
      componentProps: {
        initial: this.activeWorkoutProgram[type],
        clear: type === "endDate",
        includeTime: true,
        dateType: type,
      }
    });
    await popover.present();
    const { role, data } = await popover.onDidDismiss();
    if(role === "cleared" || role === "set") {
      const toUpdate = data || null;
      this.activeWorkoutProgram[type] = toUpdate;
      this.saveWorkoutProgram();
    }
  }
}
